import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import { AvatarDropdown, AvatarName, Footer, SelectLang } from '@/components';
import AntdThemeSync from '@/components/AntdThemeSync';
import ForceChangePassword from '@/components/ForceChangePassword';
import { ARGUS_TOKEN_KEY, ARGUS_USER_KEY } from '@/constants/storage';
import { meApiAuthMeGet } from '@/services/swagger/auth';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const getUserFromStorage = (): API.CurrentUser | undefined => {
    if (typeof localStorage === 'undefined') return undefined;
    const raw = localStorage.getItem(ARGUS_USER_KEY);
    if (!raw) return undefined;
    try {
      return JSON.parse(raw) as API.CurrentUser;
    } catch {
      return undefined;
    }
  };

  const fetchUserInfo = async () => {
    const localUser = getUserFromStorage();
    if (localUser) return localUser;
    const token =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem(ARGUS_TOKEN_KEY)
        : null;
    if (!token) return undefined;
    try {
      const msg = await meApiAuthMeGet({
        skipErrorHandler: true,
      });
      if (msg?.data) {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(ARGUS_USER_KEY, JSON.stringify(msg.data));
        }
        return msg.data;
      }
    } catch (_error) {
      // 当前后端可能未提供 currentUser 接口，保留在登录页由用户主动登录。
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [<SelectLang key="SelectLang" />],
    avatarProps: {
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev ? [] : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          <AntdThemeSync />
          <ForceChangePassword />
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
import { isDemoMode } from '@/demo';

/** 开发环境或 Vercel Demo 构建走相对路径 /api（Demo 由 request 拦截器 Mock） */
const useLocalApi = isDemoMode() || process.env.NODE_ENV === 'development';

export const request: RequestConfig = {
  baseURL: useLocalApi ? '' : 'https://proapi.azurewebsites.net',
  ...errorConfig,
};
